import { useLocation, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useUserDetails } from './hooks/useUserDetails';
import { useUserMedias } from './hooks/useUserMedia';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import type { RootState } from '@/presentation/store';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { Navigator } from '@/presentation/routing/Navigator';
import { ChatUtils } from '@/domain/utils/chatUtils';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentGridItem } from '@/presentation/components/common/content/ContentGridItem';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserActionsSheet } from '../content/components/sheets/userActions/UserActionsSheet';
import { SelectContentTypeSheet } from '@/presentation/feature/publish/components/sheets/selectContentType/SelectContentTypeSheet';
import { AddMediaButton } from '@/presentation/components/common/controls/AddMediaButton';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { UserDetailsHeader } from './components/UserDetailsHeader';
import { UserDetailsEmptyOwnVideos } from './components/UserDetailsEmptyOwnVideos';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { SubscribeUserSheet } from './components/subscribeUserSheet/SubscribeUserSheet';
import { AccountInfoSheet } from '@/presentation/components/common/sheet/accountInfo/AccountInfoSheet';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * User Details Page
 * Shows user profile with grid of media thumbnails
 * 
 * Architecture: Component → Hook → UseCase → Service
 * 
 * Migrated from Flutter's user_details_screen.dart
 * Main features:
 * - Fetches and displays user profile information
 * - Displays user's media content in a grid
 * - Checks if viewing current user vs another user
 * - Handles loading and error states
 */
export const UserDetailsPage = () => {
  const { userId: rawUserId } = useParams<{ userId: string }>();

  // If the param starts with @, it's a mention/username lookup (e.g., /@john)
  // Otherwise, it's a Firebase ID (e.g., /abc123)
  const isMention = rawUserId?.startsWith('@') ?? false;
  const userId = isMention ? undefined : rawUserId;
  const mention = isMention ? rawUserId!.substring(1) : undefined;
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const gridColumnsCount = useGridColumnsCount();

  // Get current user from global state
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Get navigation screen key from RouteStack context
  // This key is stable for this route instance and used for both navigation and query caching
  // IMPORTANT: Capture the screen key ONCE on mount and never change it
  // Initialize the ref with a value IMMEDIATELY to avoid undefined on first render
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  // Log on first render only
  const hasLoggedRef = useRef(false);
  if (!hasLoggedRef.current) {
    // console.log('🔑 [UserDetailsPage] Captured screenKey on mount:', screenKey);
    hasLoggedRef.current = true;
  }

  // Memoize cacheKey to prevent creating new array on every render
  // which would cause React Query to see it as a different query
  const cacheKey = useMemo(
    () => {
      const key = [screenKey, 'userDetails'];
      return key;
    },
    [screenKey]
  );

  // Use feature hooks for user details and medias
  // Manual fetching - we'll trigger in onInit lifecycle
  // The React Query cache key includes the screenKey for proper caching behavior
  // The React Query will be removed inside RouteStack when the route is removed from stack / cache
  const {
    userData,
    isLoading: isLoadingUser,
    error: userError,
    resolvedUserId,
    isCurrentUser,
    subscribeLabel,
    fetchUser,
  } = useUserDetails({ userId, mention }, cacheKey);

  const { medias, isLoading: isLoadingMedias, isLoadingMore: isLoadingMoreMedias, hasMore, error: mediasError, fetchMedias, loadMore } = useUserMedias(resolvedUserId, isCurrentUser);

  // When navigating by mention: after user is resolved (resolvedUserId becomes available),
  // trigger media fetch sequentially. Matches Flutter's _tryInit where getUserByUsername
  // is awaited before _getMedias is called.
  const hasTriggeredMentionMediaFetchRef = useRef(false);
  useEffect(() => {
    if (mention && resolvedUserId && !hasTriggeredMentionMediaFetchRef.current) {
      hasTriggeredMentionMediaFetchRef.current = true;
      fetchMedias();
    }
  }, [mention, resolvedUserId, fetchMedias]);

  useBasePage({
    screenKey, // Pass screenKey to differentiate between multiple UserDetailsPage instances
    onInit: () => {
      // Initialize data on first mount
      // Wrap async logic in an IIFE since onInit is not async
      (async () => {
        if (userId) {
          // userId available: fetch user and medias in parallel (existing behavior)
          await Promise.all([
            fetchUser(),
            fetchMedias(),
          ]);
        } else if (mention) {
          // mention only: fetch user first (medias triggered by useEffect when resolvedUserId is set)
          await fetchUser();
        }
      })();

      // Cleanup on unmount (rare with KeepAlive)
      return () => {
      };
    },
    onPush: (_repushed) => {
      // Called when first navigating to this page
      // Data already loaded in onInit
      // console.log('🧹 [UserDetailsPage] onPush - ', screenKey);
    },
    onPopNext: () => {
      // Called when returning to this page (user pressed back)
      // Data should be cached, no need to refetch
    },
    onPushNext: () => {
      // Called when leaving this page (new route pushed on top)
    },
    onPop: () => {
      // Called when this page is being popped (going back from this page)
      // console.log('🧹 [UserDetailsPage] onPop - ', screenKey);
    },
    onDispose: () => {
      // Clean up React Query cache for this component instance
    },
    // Automatically clean React Query cache on POP (default value but for readability)
    cleanReactQueryBehaviour: 'onPop',
    debug: false, // Set to true to see lifecycle logs during development
  });

  const handleBack = () => {
    Navigator.goBack();
  };

  // Flutter's profile "+" calls the shared `navigateToAddContentScreen`
  // (`user_details_screen.dart:914`), whose first statement is the
  // upload-in-progress guard (`navigation_service.dart:420-435`). Web inserts a
  // web-only select-content-type sheet in between, so the guard runs here — at the
  // same tap Flutter gates — via the shared `canOpenAddContent` helper (which also
  // carries the guard sheet's QA locator).
  const handleAddContentClick = useCallback(() => {
    if (!canOpenAddContent({ l10n, screenKey })) return;
    SelectContentTypeSheet.show(
      {
        title: l10n.publish.uploadVideoTitle,
        description: l10n.publish.uploadVideoDescription,
        canGoLive: true,
      },
      undefined,
      screenKey,
      responsive.selectContentTypeSheet.width,
      responsive.selectContentTypeSheet.fixedHeight,
    );
  }, [l10n, screenKey, responsive.selectContentTypeSheet.width, responsive.selectContentTypeSheet.fixedHeight]);

  const handleMediaClick = useCallback((media: UserMediaEntity) => {
    // Block navigation for items that aren't ready to play yet — local
    // (just-published, no Firestore data) or still being transcoded.
    if (media.isLocal || media.isProcessing) {
      InfoSheet.show({
        title: l10n.content.processingTitle,
        infoText: l10n.content.processingDescription,
        screenKey,
      });
      return;
    }

    // Strip non-playable items so the user-videos page doesn't try to render them.
    const playableMedias = medias.filter(m => !m.isLocal && !m.isProcessing);
    const currentIndex = playableMedias.findIndex(m => m.id === media.id);

    Navigator.navigateToUserVideos(resolvedUserId!, {
      medias: playableMedias,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
    });
  }, [medias, resolvedUserId, l10n, screenKey]);

  const renderMediaItem = useCallback(
    (media: UserMediaEntity) => (
      <div {...qaAttr({ id: `user-media-${media.id}` })} style={{ display: 'contents' }}>
        <ContentGridItem media={media} onClick={handleMediaClick} />
      </div>
    ),
    [handleMediaClick],
  );

  const notImplementedYet = useCallback(() => {
    InfoSheet.showNotImplementedYet({ l10n, screenKey });
  }, [l10n, screenKey]);

  const handleSubscribePress = useCallback(() => {
    SubscribeUserSheet.show({
      targetFirebaseId: resolvedUserId!,
      preloadedUser: userData,
      screenKey,
    });
  }, [resolvedUserId, userData, screenKey]);

  const handleShowAccountInfo = useCallback(() => {
    AccountInfoSheet.show({ screenKey });
  }, [screenKey]);

  const handleMoreActions = useCallback(() => {
    UserActionsSheet.show(
      undefined,
      resolvedUserId,
      null,
      screenKey,
      responsive.userActionsSheet.fixedHeight,
      // UserDetailsPage app-bar/avatar entry: include the owner account-management
      // items (Edit profile, Settings, Help & About, Log out, Delete account).
      true,
    );
  }, [resolvedUserId, screenKey, responsive.userActionsSheet.fixedHeight]);

  // Mirrors Flutter's user_details_screen.dart secondary-press target — builds
  // the `ChatUsersInfo` struct (including `currentName`, now required by the
  // chat-metadata write in `sendTextChatMessageUseCase`) and pushes the real
  // chat page. `userData` is guaranteed by the early returns above the JSX
  // for the loading/error/blocked branches, but it can still be transiently
  // null while the initial fetch resolves; short-circuit defensively.
  const handleChatPress = useCallback(() => {
    if (!currentUser || !userData) {
      return;
    }
    const chatUsersInfo: ChatUsersInfoEntity = {
      currentFirebaseId: currentUser.firebaseId,
      peerId: userData.firebaseId,
      currentName: currentUser.name,
      peerName: userData.name,
      createdTimestamp: 0,
      groupChatId: ChatUtils.getGroupChatId(currentUser.firebaseId, userData.firebaseId),
    };
    Navigator.navigateToChat(chatUsersInfo);
  }, [currentUser, userData]);

  // Mirrors Flutter's `navigateToCommunityScreen(User user)` — forwards the
  // already-resolved `UserEntity` to `/community/:userId`. Flutter does not gate
  // the community button itself (the access placeholder lives inside the
  // CommunityScreen via `_hasAccess`), so navigate directly; the page owns the
  // gating (Task 13). `userData` is guaranteed by the loading/error/blocked
  // early returns above the JSX but can be transiently null mid-fetch —
  // short-circuit defensively.
  const handleCommunityPress = useCallback(() => {
    if (!userData) {
      return;
    }
    Navigator.navigateToCommunity(userData);
  }, [userData]);

  // All hooks above. Guard auth after this point — no hooks may follow.
  if (!currentUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ gap: responsive.gap.xs }}>
        <p style={{ color: theme.text.primary, fontWeight: responsive.fontWeight.semibold }}>{l10n.userDetails.notLoggedInTitle}</p>
        <p style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>{l10n.userDetails.notLoggedInMessage}</p>
      </div>
    );
  }

  // Show error state with retry button
  if (userError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ padding: responsive.spacing.xl, gap: responsive.gap.lg }}>
        <div style={{ color: theme.danger }} className="text-center">
          <p style={{ fontWeight: responsive.fontWeight.semibold, marginBottom: responsive.spacing.xxs }}>{l10n.userDetails.loadErrorTitle}</p>
          <p style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>
            {userError instanceof Error ? userError.message : l10n.common.unknownError}
          </p>
        </div>
        <button
          onClick={() => fetchUser()}
          disabled={isLoadingUser}
          className="flex items-center transition-colors"
          style={{
            backgroundColor: theme.accent,
            color: theme.white,
            opacity: isLoadingUser ? 0.6 : 1,
            gap: responsive.gap.xs,
            padding: responsive.button.padding.small,
            borderRadius: responsive.borderRadius.lg,
          }}
        >
          {isLoadingUser && <ExpauseLoadingIndicator />}
          {isLoadingUser ? l10n.common.retrying : l10n.common.tryAgain}
        </button>
      </div>
    );
  }

  // Handle blocked or suspended accounts (only once data is loaded)
  if (userData?.blocked || userData?.suspended) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center"
        style={{ padding: responsive.spacing.xl }}>
        <div style={{ color: theme.text.primary }} className="text-center">
          <p style={{ fontWeight: responsive.fontWeight.semibold, marginBottom: responsive.spacing.xxs }}>
            {isCurrentUser ? l10n.userDetails.accountBlocked : l10n.userDetails.accountNotAvailable}
          </p>
          <p style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>
            {l10n.userDetails.accountUnavailable}
          </p>
        </div>
      </div>
    );
  }

  const profileFlexibleBackground = (
    <UserDetailsHeader
      userData={userData ?? null}
      resolvedUserId={resolvedUserId!}
      isCurrentUser={isCurrentUser}
      subscribeLabel={subscribeLabel}
      onSubscribePress={isCurrentUser ? handleShowAccountInfo : handleSubscribePress}
      onCommunityPress={handleCommunityPress}
      onSecondaryPress={isCurrentUser ? notImplementedYet : handleChatPress}
      onAvatarPress={handleMoreActions}
    />
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'user-details', status: isLoadingUser ? 'loading' : 'ready' }}
      listQa={{ id: 'user-details-videos' }}
      items={medias}
      renderItem={renderMediaItem}
      getItemKey={(media) => media.id}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMoreMedias}
      addItemComponent={
        isCurrentUser && medias.length > 0 ? (
          <div
            className="flex items-center justify-center"
            style={{
              width: '100%',
              backgroundColor: theme.surface,
            }}
          >
            <AddMediaButton
              qa={{ id: 'user-details-add-content' }}
              onTap={handleAddContentClick}
              backgroundColor={theme.transparent}
              iconBackgroundColor={theme.secondary}
              buttonSize={responsive.button.size.xxxl}
              iconSize={responsive.icon.xlarge}
            />
          </div>
        ) : undefined
      }
      emptyListComponent={
        isLoadingMedias ? (
          <div className="flex justify-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <ExpauseLoadingIndicator />
          </div>
        ) : mediasError ? (
          <div className="text-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <p style={{ color: theme.danger }}>{l10n.content.errorFetchingFeed}</p>
          </div>
        ) : isCurrentUser ? (
          <UserDetailsEmptyOwnVideos
            onAddVideo={handleAddContentClick}
            onShareApp={notImplementedYet}
          />
        ) : (
          <div className="text-center" style={{ padding: `${responsive.spacing['3xl']}px 0` }}>
            <p style={{ color: theme.text.secondary }}>{l10n.userDetails.noVideos}</p>
          </div>
        )
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: userData?.name ?? l10n.common.emptyPlaceholder,
        titleExtra: resolvedUserId ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: responsive.gap.xs, verticalAlign: 'middle' }}>
            <IdentityBadge userId={resolvedUserId} />
          </span>
        ) : undefined,
        // UserDetailsHeader has its own title display, so keep the appbar title always visible to avoid jarring fade
        alwaysShowTitle: true,
        onLeadingTap: handleBack,
        actions: (
          <div className="flex items-center" style={{ gap: responsive.gap.lg }}>
            <button
              type="button"
              {...qaAttr({ id: 'user-details-actions' })}
              onClick={handleMoreActions}
              className="hover:opacity-80 transition-opacity focus:outline-none"
              aria-label="More options"
            >
              <AssetIcon path="/assets/vectors/more.svg" />
            </button>
          </div>
        ),
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: profileFlexibleBackground,
        },
      }}
    />
  );
};
